import { normalizeProductCode, validateSku } from '../../domain/catalogValidation';
import {
  parseCsvBooleanStrict,
  parseCsvItemTypeStrict,
  parseOptionalNumber,
} from '../../domain/csv/csvParseHelpers';
import { type MasterMatchCandidate } from '../../domain/csv/masterMatching';
import {
  parseBarcodeForCsvCell,
  productCsvRowChangeReasons,
  productCsvRowWouldChangeOnUpdate,
} from './catalogCsvProductDelta';
import {
  barcodeCsvPreviewNote,
  CSV_UNMATCHED_BRAND_MESSAGE,
  CSV_UNMATCHED_CATEGORY_MESSAGE,
  CSV_UNMATCHED_SUPPLIER_MESSAGE,
  loadBrandCandidates,
  loadCategoryCandidates,
  loadSupplierCandidates,
  logCatalogCsvBarcodeDebug,
  previewRow,
  resolveBrandFromRecord,
  resolveCategoryFromRecord,
  resolveSupplierFromRecord,
  unmatchedMasterMessage,
  type CsvPreviewRowOutcome,
  type ParsedRow,
} from './catalogCsvImportHelpers';
import { findProductBySku } from './catalogCsvImportLookup';
import { parseVariantFromCsvRow } from './catalogCsvVariantParse';

export type ProductCsvPreviewContext = {
  categoryCandidates: MasterMatchCandidate[];
  brandCandidates: MasterMatchCandidate[];
  supplierCandidates: MasterMatchCandidate[];
};

export async function loadProductCsvPreviewContext(): Promise<ProductCsvPreviewContext> {
  const [categoryCandidates, brandCandidates, supplierCandidates] = await Promise.all([
    loadCategoryCandidates(),
    loadBrandCandidates(),
    loadSupplierCandidates(),
  ]);
  return { categoryCandidates, brandCandidates, supplierCandidates };
}

export async function previewProductCsvRow(
  rowNumber: number,
  record: ParsedRow,
  ctx: ProductCsvPreviewContext
): Promise<CsvPreviewRowOutcome> {
  const skuRaw = record.sku?.trim();
  if (!skuRaw) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', 'Sin SKU', 'SKU obligatorio.'),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      newProductCount: 0,
    };
  }
  const sku = normalizeProductCode(skuRaw);
  const skuErr = validateSku(sku);
  if (skuErr) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', sku, skuErr),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      newProductCount: 0,
    };
  }

  // #712: Si la fila tiene columnas de variante, es una fila de variante.
  // No requiere nombre/precio (esos van en la fila del parent).
  const hasVariantColumns = Boolean(
    record.variante_eje_1?.trim() || record.variante_valor_1?.trim() ||
    record.variante_eje_2?.trim() || record.variante_valor_2?.trim() ||
    record.variante_eje_3?.trim() || record.variante_valor_3?.trim()
  );
  if (hasVariantColumns) {
    try {
      const variantDraft = parseVariantFromCsvRow(record, 0);
      if (!variantDraft) {
        return {
          row: previewRow(rowNumber, 'error', 'validar', sku, 'Variante inválida: revisá ejes y valores.'),
          okCount: 0,
          skipCount: 0,
          errorCount: 1,
          newProductCount: 0,
        };
      }
      const detail = `${sku} · variante: ${variantDraft.nameSuffix}`;
      return {
        row: previewRow(rowNumber, 'ok', 'variante', detail),
        okCount: 1,
        skipCount: 0,
        errorCount: 0,
        newProductCount: 0,
      };
    } catch (e) {
      return {
        row: previewRow(rowNumber, 'error', 'validar', sku, e instanceof Error ? e.message : 'Variante inválida.'),
        okCount: 0,
        skipCount: 0,
        errorCount: 1,
        newProductCount: 0,
      };
    }
  }

  const existing = await findProductBySku(sku);
  let newProductCount: 0 | 1 = 0;
  if (!existing) {
    const nombre = record.nombre?.trim();
    const precio = parseOptionalNumber(record.precio);
    if (!nombre || precio == null || precio <= 0) {
      return {
        row: previewRow(
          rowNumber,
          'error',
          'validar',
          sku,
          'Para un producto nuevo hacen falta nombre y precio mayor a 0.'
        ),
        okCount: 0,
        skipCount: 0,
        errorCount: 1,
        newProductCount: 0,
      };
    }
    newProductCount = 1;
  }
  const isUpdate = Boolean(existing);
  const barcodeParsed = parseBarcodeForCsvCell(record.codigo_barras, { isUpdate });
  const barcodeNote = existing ? barcodeCsvPreviewNote(barcodeParsed) : undefined;
  logCatalogCsvBarcodeDebug(sku, 'preview', record.codigo_barras, {
    rowNumber,
    parse: barcodeParsed,
    existingDbBarcode: existing?.barcode ?? null,
    hint: barcodeNote,
  });
  if (barcodeParsed.error) {
    const msg = barcodeParsed.error;
    return {
      row: previewRow(rowNumber, 'error', 'validar', sku, msg),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      trackErrorMessage: msg,
      newProductCount: 0,
    };
  }
  const tipoCell = parseCsvItemTypeStrict(record.tipo);
  if (tipoCell.error) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', sku, tipoCell.error),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      trackErrorMessage: tipoCell.error,
      newProductCount: 0,
    };
  }
  const activoCell = parseCsvBooleanStrict(record.activo);
  if (activoCell.error) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', sku, activoCell.error),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      trackErrorMessage: activoCell.error,
      newProductCount: 0,
    };
  }
  const categoryMsg = unmatchedMasterMessage(
    resolveCategoryFromRecord(record, ctx.categoryCandidates),
    Boolean(record.categoria_id?.trim() || record.categoria?.trim()),
    CSV_UNMATCHED_CATEGORY_MESSAGE
  );
  if (categoryMsg) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', sku, categoryMsg),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      trackErrorMessage: categoryMsg,
      newProductCount: 0,
    };
  }
  const brandMsg = unmatchedMasterMessage(
    resolveBrandFromRecord(record, ctx.brandCandidates),
    Boolean(record.marca_id?.trim() || record.marca?.trim()),
    CSV_UNMATCHED_BRAND_MESSAGE
  );
  if (brandMsg) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', sku, brandMsg),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      trackErrorMessage: brandMsg,
      newProductCount: 0,
    };
  }
  const supplierMsg = unmatchedMasterMessage(
    resolveSupplierFromRecord(record, ctx.supplierCandidates),
    Boolean(record.proveedor_preferido_id?.trim() || record.proveedor_preferido?.trim()),
    CSV_UNMATCHED_SUPPLIER_MESSAGE
  );
  if (supplierMsg) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', sku, supplierMsg),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      trackErrorMessage: supplierMsg,
      newProductCount: 0,
    };
  }
  if (existing) {
    const changeCtx = {
      categoryCandidates: ctx.categoryCandidates,
      brandCandidates: ctx.brandCandidates,
      supplierCandidates: ctx.supplierCandidates,
    };
    const wouldChange = productCsvRowWouldChangeOnUpdate(existing, record, changeCtx);
    if (!wouldChange) {
      return {
        row: previewRow(rowNumber, 'skip', 'omitir', sku),
        okCount: 0,
        skipCount: 1,
        errorCount: 0,
        newProductCount: 0,
      };
    }
    const reasons = productCsvRowChangeReasons(existing, record, changeCtx);
    const detail = reasons.length > 0 ? `${sku} · ${reasons.join(', ')}` : sku;
    return {
      row: previewRow(rowNumber, 'ok', 'actualizar', detail, barcodeNote),
      okCount: 1,
      skipCount: 0,
      errorCount: 0,
      newProductCount: 0,
    };
  }
  return {
    row: previewRow(rowNumber, 'ok', 'crear', sku),
    okCount: 1,
    skipCount: 0,
    errorCount: 0,
    newProductCount,
  };
}
